import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useContext, forwardRef, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Button from '@base/Button/Button';
import { register } from '@actions/summit';
import FreePlanUnlock from '@comp/FreePlanUnlock/FreePlanUnlock';
import { useDispatchToStore, useSelectorToStore } from '@hooks/reduxHooks';
import deviceHelper from '@utils/deviceHelper';
import dateHelper from '@utils/dates';
import { sendEvents } from '@events/sendAnalytics';
import { segment } from '@actions/analytics';
import { SummitRegister } from '@events/events';
import { LearnerId, LearnerUserName } from '@events/eventProps';
import { unlockFreePlanFor } from '@constants/index';
import { Context as IsMobileContext } from '@context/IsMobileContext';
import { Spacings, Fonts } from '@styles/index';
import { scrollToRef } from './sections';

const StyledButton = styled(Button)`
  margin-top: ${Spacings.SPACING_4B};
  width: auto;
  height: auto;
  color: ${({ $isRegistered }) => ($isRegistered ? '#2d81f7' : '#fcfcfc')};
  background-color: ${({ $isRegistered }) =>
    $isRegistered ? 'transparent' : '#2d81f7'};
  border: ${({ $isRegistered }) =>
    $isRegistered ? '2px solid #2d81f7' : 'none'};
  border-radius: ${Spacings.SPACING_2B};
  font-size: ${Fonts.H4};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 25px;
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_8B};
  &:active {
    background-color: ${({ $isRegistered }) =>
      $isRegistered ? 'transparent' : '#2d81f7'};
    border: ${({ $isRegistered }) =>
      $isRegistered ? '2px solid #2d81f7' : 'none'};
  }
`;

const RegisterButton = forwardRef(({ className, scheduleRef }, ref) => {
  const isMobileDevice = useContext(IsMobileContext);
  const dispatch = useDispatchToStore();
  const { query } = useRouter();
  const { summitKey } = query;

  const [freePlanUnlockDrawerState, setFreePlanUnlockDrawerState] = useState(
    false
  );
  const closeFreePlanUnlockDrawer = () => setFreePlanUnlockDrawerState(false);

  const isRegistered = useSelector(
    ({ summit }) => summit.isRegistered || false
  );
  const summitUid = useSelector(({ summit }) => summit.info?.uid || '');
  const isLoggedIn = useSelector(({ auth }) => !!auth.me?.uid || false);
  const userUid = useSelector(({ auth }) => auth.me?.uid || '');
  const username = useSelector(({ auth }) => auth.me?.username || '');
  const isPlatformUnlocked = useSelector(
    ({ auth }) => auth.me?.platformUnlockStatus?.is_platform_unlocked
  );
  const [showWatchEvents, setShowWatchEvents] = useState(false);
  const dates = useSelectorToStore(({ summit }) => {
    return summit.schedule.map((daySchedule) => daySchedule.date);
  });
  useEffect(() => {
    let watchEvents = isRegistered;
    if (!watchEvents) {
      const today = new Date();
      watchEvents = dates.every(
        (date) => dateHelper.compareDates(date, today) === 1
      );
    }
    setShowWatchEvents(watchEvents);
  }, [dates, isRegistered]);
  const onClick = () => {
    if (showWatchEvents) {
      scrollToRef(scheduleRef, isMobileDevice);
    } else {
      if (deviceHelper.isMobileDevice() && !isLoggedIn) {
        window.location.href = `/login?redirect=/event/${summitKey}`;
        return;
      }
      if (isLoggedIn && !isPlatformUnlocked) {
        setFreePlanUnlockDrawerState(true);
      } else {
        dispatch(register(summitUid)).then((res) => {
          if (res?.result?.success_code) {
            const eventData = {
              [LearnerId]: userUid || 'anonymous',
              [LearnerUserName]: username || 'anonymous',
              'Summit Uid': summitUid,
              'Summit Name': summitKey?.toLowerCase() || ''
            };
            sendEvents(SummitRegister, eventData, true);
            segment.track(SummitRegister, eventData);
          }
        });
      }
    }
  };
  return (
    <>
      <StyledButton
        className={className}
        label={showWatchEvents ? 'Watch events' : 'Register now'}
        $isRegistered={showWatchEvents}
        onClick={onClick}
        ref={ref}
      />
      <FreePlanUnlock
        drawerState={freePlanUnlockDrawerState}
        closeDrawerHandler={closeFreePlanUnlockDrawer}
        objectType={unlockFreePlanFor.SUMMIT}
        objectUid={summitUid}
      />
    </>
  );
});

RegisterButton.displayName = 'RegisterButton';

RegisterButton.propTypes = {
  className: PropTypes.string,
  scheduleRef: PropTypes.objectOf(PropTypes.object)
};

RegisterButton.defaultProps = {
  className: '',
  scheduleRef: null
};

export default RegisterButton;
